import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { Box } from "@mui/material";

import useLocalization from "../../hooks/useLocalization";
import useEntity from "../../hooks/useEntity";
import useBrowser from "../../hooks/useBrowser";
import useData from "../../hooks/useData";
import usePage from "../../hooks/usePage";

import Input from "../elements/Input";
import AddressEditor from "./AddressEditor";
import EditorToolbar from "./EditorToolbar";
import Switch from "../elements/Switch";
import Select from "../elements/Select";
import {
  Typography,
  Divider,
  IconButton,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  ContentCopy,
  ExpandMore,
  Add,
  DeleteOutline,
} from "@mui/icons-material";

const UserEditor = ({ userId, onSave }) => {
  const currentUserRole = useSelector((state) => state.authentication.role);
  const { getLabel, provinces } = useLocalization();
  const {
    setEntity,
    emptyObject,
    entityData,
    setEntityData,
    clearEntityData,
    getEntityData,
    validateEntityData,
    updateField,
    message,
    isSuccessful,
    getFieldMessage,
    saved,
  } = useEntity();

  const { getFromServer, executeAction } = useData();
  const { showMessage } = usePage();

  const { windowHeight } = useBrowser();
  const [showPassword, setShowPassword] = useState(false);
  const [supervisors, setSupervisors] = useState([]);
  const [clients, setClients] = useState([]);
  const [territoryLabel, setTerritoryLabel] = useState("-");

  useEffect(() => {
    setEntity("User");
  }, []);

  useEffect(() => {
    const loadSupervisors = async () => {
      const results = await getFromServer({
        entityName: "User",
        filter: { role: { $in: ["supervisor", "admin"] }, isActive: true },
      });

      const list = Array.isArray(results) ? results : [];
      list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setSupervisors(list);
    };

    loadSupervisors();
  }, []);

  useEffect(() => {
    const loadClients = async () => {
      const results = await getFromServer({
        entityName: "Client",
        filter: { isActive: true },
      });

      const list = Array.isArray(results) ? results : [];
      list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setClients(list);
    };

    loadClients();
  }, []);

  useEffect(() => {
    if (!userId) {
      clearEntityData();
    } else getEntityData(userId);
  }, [emptyObject]);

  useEffect(() => {
    const loadSyndicatedTerritories = async () => {
      const targetUserId = userId || entityData?.id;
      if (!targetUserId) {
        setTerritoryLabel("-");
        return;
      }

      try {
        const result = await executeAction({
          entityName: "User",
          action: "getTerritoryAssignments",
          data: { userId: targetUserId },
        });

        if (!result?.isSuccessful) {
          setTerritoryLabel("-");
          return;
        }

        const territories = Array.isArray(result?.data?.territories)
          ? result.data.territories
          : [];

        const syndicatedNames = [
          ...new Set(
            territories
              .filter((territory) => {
                const syndicationId = territory?.syndicationId;
                return syndicationId !== null && syndicationId !== undefined;
              })
              .map((territory) => String(territory?.name || "").trim())
              .filter((name) => !!name),
          ),
        ];

        setTerritoryLabel(
          syndicatedNames.length > 0 ? syndicatedNames.join(", ") : "-",
        );
      } catch (err) {
        setTerritoryLabel("-");
      }
    };

    loadSyndicatedTerritories();
  }, [userId, entityData?.id]);

  const handleSave = async () => {
    if (entityData?.role === "client" && !entityData?.clientId) {
      showMessage("Client users must be assigned to a client", "error");
      return;
    }

    if (entityData?.role === "teamLead" && !entityData?.supervisorId) {
      showMessage("Team Lead users must be assigned to a supervisor", "error");
      return;
    }

    validateEntityData();
  };

  useEffect(() => {
    if (!saved) return;
    onSave();
  }, [saved]);

  // Helper function to update nested fields
  const updateNestedField = (path, value) => {
    const keys = path.split(".");
    const updatedData = { ...entityData };

    // Initialize nested objects if they don't exist
    if (!updatedData.permissions) updatedData.permissions = {};
    if (!updatedData.notifications) updatedData.notifications = {};

    let current = updatedData;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) {
        current[keys[i]] = {};
      } else {
        current[keys[i]] = { ...current[keys[i]] };
      }
      current = current[keys[i]];
    }

    current[keys[keys.length - 1]] = value;
    setEntityData(updatedData);
  };

  const normalizeDateInput = (value) => {
    if (!value) return "";
    if (value instanceof Date) return value.toISOString().split("T")[0];

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toISOString().split("T")[0];
  };

  const getVacationRanges = () =>
    Array.isArray(entityData.vacationRanges) ? entityData.vacationRanges : [];

  const addVacationRange = () => {
    const ranges = getVacationRanges();
    updateField("vacationRanges", [...ranges, { startDate: "", endDate: "" }]);
  };

  const updateVacationRange = (index, field, value) => {
    const ranges = getVacationRanges().map((range, rangeIndex) =>
      rangeIndex === index ? { ...range, [field]: value } : range,
    );
    updateField("vacationRanges", ranges);
  };

  const removeVacationRange = (index) => {
    const ranges = getVacationRanges().filter(
      (_, rangeIndex) => rangeIndex !== index,
    );
    updateField("vacationRanges", ranges);
  };

  const provinceItems = useMemo(
    () => (Array.isArray(provinces) ? provinces : []),
    [provinces],
  );

  if (!entityData) return;

  const vacationRanges = getVacationRanges();
  const isSysAdmin = currentUserRole === "sysadmin";
  const isRestrictedTargetRole =
    entityData.role === "finance" || entityData.role === "sysadmin";
  const displayedName =
    String(entityData.legalName || "").trim() ||
    String(entityData.name || "").trim() ||
    "-";
  const supervisorValue = entityData.supervisorId || "";

  const handleCopyDisplayedName = async () => {
    if (!displayedName || displayedName === "-") return;

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(displayedName);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = displayedName;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }

      showMessage("Name copied", "success");
    } catch (err) {
      console.error("Failed to copy displayed user name:", err);
      showMessage("Unable to copy name", "error");
    }
  };

  const baseRoleItems = [
    { id: "admin", name: "Admin" },
    { id: "hr", name: "HR" },
    { id: "supervisor", name: "Supervisor" },
    { id: "teamLead", name: "Team Lead" },
    { id: "rep", name: "Rep" },
    { id: "client", name: "Client" },
    { id: "qa", name: "QA" },
  ];

  const privilegedRoleItems = [
    { id: "finance", name: "Finance" },
    { id: "sysadmin", name: "Sysadmin" },
  ];

  let roleItems = isSysAdmin
    ? [...baseRoleItems, ...privilegedRoleItems]
    : [...baseRoleItems];

  if (
    !isSysAdmin &&
    isRestrictedTargetRole &&
    !roleItems.some((role) => role.id === entityData.role)
  ) {
    roleItems = [
      ...roleItems,
      {
        id: entityData.role,
        name: entityData.role === "finance" ? "Finance" : "Sysadmin",
      },
    ];
  }

  return (
    <Box
      width="100%"
      height="100%"
    >
      <EditorToolbar
        onSave={handleSave}
        message={entityData.id}
      />
      <Box
        width="calc(100% - 2rem)"
        height="calc(100% - 2rem - 30px)"
        padding="1rem"
        display="flex"
        flexDirection="column"
        gap={1}
        overflow="auto"
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700 }}
          >
            {displayedName}
          </Typography>
          <IconButton
            size="small"
            onClick={handleCopyDisplayedName}
            aria-label="Copy displayed name"
          >
            <ContentCopy fontSize="small" />
          </IconButton>
        </Box>

        <Box
          sx={{ display: "flex" }}
          gap={1}
        >
          <Box sx={{ flex: 1 }}>
            <Input
              id="name"
              label={getLabel("name")}
              value={entityData.name}
              onChange={(newValue) => updateField("name", newValue)}
              required
              fullwidth
              {...getFieldMessage("name")}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Select
              label={getLabel("role")}
              value={entityData.role || "rep"}
              items={roleItems}
              disabled={!isSysAdmin && isRestrictedTargetRole}
              onChange={(newValue) => updateField("role", newValue)}
            />
          </Box>
        </Box>
        <Box
          sx={{ display: "flex" }}
          gap={1}
        >
          <Box sx={{ flex: 1 }}>
            <Select
              label={getLabel("supervisor") || "Supervisor"}
              value={supervisorValue}
              items={supervisors}
              includeNone={entityData.role !== "teamLead"}
              noneLabel={getLabel("none") || "None"}
              onChange={(newValue) =>
                updateField(
                  "supervisorId",
                  newValue === "all" || newValue === "" ? null : newValue,
                )
              }
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Select
              label={getLabel("language")}
              value={entityData.language ? entityData.language : "en"}
              items={[
                { id: "en", name: "English" },
                { id: "fr", name: "French" },
              ]}
              onChange={(newValue) => updateField("language", newValue)}
            />
          </Box>
        </Box>
        {entityData.role === "teamLead" ? (
          <Box
            sx={{ display: "flex" }}
            gap={1}
          >
            <Box sx={{ flex: 1 }}>
              <Select
                label={getLabel("assignedProvinces") || "Assigned Provinces"}
                value={
                  Array.isArray(entityData.assignedProvinces)
                    ? entityData.assignedProvinces
                    : []
                }
                items={provinceItems}
                multiSelect
                onChange={(newValue) =>
                  updateField(
                    "assignedProvinces",
                    Array.isArray(newValue) ? newValue : [],
                  )
                }
              />
            </Box>
            <Box sx={{ flex: 1 }} />
          </Box>
        ) : null}
        {entityData.role === "client" ? (
          <Box
            sx={{ display: "flex" }}
            gap={1}
          >
            <Box sx={{ flex: 1 }}>
              <Select
                label={getLabel("clients") || "Client"}
                value={entityData.clientId || ""}
                items={clients}
                onChange={(newValue) =>
                  updateField("clientId", newValue || null)
                }
              />
            </Box>
            <Box sx={{ flex: 1 }} />
          </Box>
        ) : null}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontWeight: 700 }}>Territory:</Typography>
          <Typography>{territoryLabel}</Typography>
        </Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Switch
            label="isActive"
            value={entityData.isActive}
            onChange={(newValue) => updateField("isActive", newValue)}
          />
        </Box>
        <Box
          display="flex"
          alignItems="center"
        >
          <Switch
            label="Allow Cellular Image Uploads"
            value={entityData.uploadImagesOnCell !== false}
            onChange={(newValue) => updateField("uploadImagesOnCell", newValue)}
          />
          <Typography
            variant="caption"
            sx={{ ml: 1, color: "text.secondary" }}
          >
            When disabled, rep can only upload photos on Wi-Fi
          </Typography>
        </Box>

        <Box
          sx={{ display: "flex" }}
          gap={1}
        >
          <Box sx={{ flex: 3 }}>
            <Input
              id="email"
              label={getLabel("email")}
              value={entityData.email}
              onChange={(newValue) => updateField("email", newValue)}
              required
              fullwidth
              {...getFieldMessage("email")}
            />
          </Box>
          <Box sx={{ flex: 2 }}>
            <Input
              id="cellPhone"
              label={getLabel("cellPhone")}
              value={entityData.cellPhone}
              onChange={(newValue) => updateField("cellPhone", newValue)}
              required
              fullwidth
              {...getFieldMessage("cellPhone")}
            />
          </Box>
        </Box>
        <Box
          sx={{ display: "flex" }}
          gap={1}
        >
          <Box sx={{ flex: 3 }}>
            <Input
              id="username"
              label={getLabel("username")}
              value={entityData.username}
              onChange={(newValue) => updateField("username", newValue)}
              required
              fullwidth
              {...getFieldMessage("username")}
            />
          </Box>
          <Box sx={{ flex: 2 }}>
            <Input
              id="password"
              label={getLabel("password")}
              value={entityData.password}
              onChange={(newValue) => updateField("password", newValue)}
              required
              fullwidth
              type={showPassword ? "text" : "password"}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
              {...getFieldMessage("password")}
            />
          </Box>
        </Box>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle1">HR / Employment</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: "flex" }}>
              <Box sx={{ flex: 1 }}>
                <Input
                  id="dateOfBirth"
                  label={getLabel("dateOfBirth")}
                  value={normalizeDateInput(entityData.dateOfBirth)}
                  onChange={(newValue) =>
                    updateField(
                      "dateOfBirth",
                      newValue === "" ? null : newValue,
                    )
                  }
                  type="date"
                  fullwidth
                  {...getFieldMessage("dateOfBirth")}
                />
              </Box>
            </Box>
            <Box
              sx={{ display: "flex" }}
              gap={1}
              mt={1}
            >
              <Box sx={{ flex: 1 }}>
                <Input
                  id="hireDate"
                  label={getLabel("hireDate")}
                  value={normalizeDateInput(entityData.hireDate)}
                  onChange={(newValue) =>
                    updateField("hireDate", newValue === "" ? null : newValue)
                  }
                  type="date"
                  fullwidth
                  {...getFieldMessage("hireDate")}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Input
                  id="terminationDate"
                  label={getLabel("terminationDate")}
                  value={normalizeDateInput(entityData.terminationDate)}
                  onChange={(newValue) =>
                    updateField(
                      "terminationDate",
                      newValue === "" ? null : newValue,
                    )
                  }
                  type="date"
                  fullwidth
                  {...getFieldMessage("terminationDate")}
                />
              </Box>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="subtitle2">Vacation Ranges</Typography>
                <Button
                  size="small"
                  startIcon={<Add />}
                  onClick={addVacationRange}
                >
                  Add Range
                </Button>
              </Box>
              <Box
                display="flex"
                flexDirection="column"
                gap={1}
                mt={1}
              >
                {vacationRanges.length === 0 && (
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    No vacation ranges added.
                  </Typography>
                )}
                {vacationRanges.map((range, index) => (
                  <Box
                    key={`vacation-range-${index}`}
                    sx={{ display: "flex", alignItems: "center" }}
                    gap={1}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Input
                        id={`vacationStart-${index}`}
                        label={getLabel("startDate")}
                        value={normalizeDateInput(range?.startDate)}
                        onChange={(newValue) =>
                          updateVacationRange(
                            index,
                            "startDate",
                            newValue === "" ? null : newValue,
                          )
                        }
                        type="date"
                        fullwidth
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Input
                        id={`vacationEnd-${index}`}
                        label={getLabel("endDate")}
                        value={normalizeDateInput(range?.endDate)}
                        onChange={(newValue) =>
                          updateVacationRange(
                            index,
                            "endDate",
                            newValue === "" ? null : newValue,
                          )
                        }
                        type="date"
                        fullwidth
                      />
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => removeVacationRange(index)}
                    >
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Input
                id="hrComments"
                label={getLabel("hrComments")}
                value={entityData.hrComments}
                onChange={(newValue) => updateField("hrComments", newValue)}
                fullwidth
                multiline
                {...getFieldMessage("hrComments")}
              />
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle1">Payroll</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box
              sx={{ display: "flex" }}
              gap={1}
            >
              <Switch
                label="includeInPayroll"
                value={!!entityData.includeInPayroll}
                onChange={(newValue) =>
                  updateField("includeInPayroll", newValue)
                }
              />
              {entityData.includeInPayroll && (
                <>
                  <Switch
                    label="allowExpenses"
                    value={!!entityData.allowExpenses}
                    onChange={(newValue) =>
                      updateField("allowExpenses", newValue)
                    }
                  />
                </>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>

        {["finance", "sysadmin", "hr"].includes(currentUserRole) && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle1">ADP Information</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                sx={{ display: "flex" }}
                gap={1}
              >
                <Box sx={{ flex: 1 }}>
                  <Input
                    id="legalName"
                    label={getLabel("legalName")}
                    value={entityData.legalName}
                    onChange={(newValue) => updateField("legalName", newValue)}
                    required
                    fullwidth
                    {...getFieldMessage("legalName")}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Input
                    id="employeeNumber"
                    label="ADP File #"
                    value={entityData.employeeNumber}
                    onChange={(newValue) =>
                      updateField("employeeNumber", newValue)
                    }
                    fullwidth
                    {...getFieldMessage("employeeNumber")}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Input
                    id="hourlyRate"
                    label={getLabel("hourlyRate")}
                    value={entityData.hourlyRate ?? ""}
                    onChange={(newValue) =>
                      updateField(
                        "hourlyRate",
                        newValue === "" ? null : Number(newValue),
                      )
                    }
                    type="number"
                    fullwidth
                    {...getFieldMessage("hourlyRate")}
                  />
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle1">Addresses</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box
              sx={{ display: "flex", flexDirection: "column" }}
              gap={2}
            >
              <AddressEditor
                title={getLabel("address") || "Primary Address"}
                value={entityData.address}
                onChange={(newValue) => updateField("address", newValue)}
              />
              <AddressEditor
                title={getLabel("courierAddress") || "Courier Delivery Address"}
                value={entityData.courierAddress}
                onChange={(newValue) => updateField("courierAddress", newValue)}
              />
            </Box>
          </AccordionDetails>
        </Accordion>

        {entityData.role === "admin" && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle1">Permissions</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography
                variant="h6"
                sx={{ mb: 1 }}
              >
                Error Monitoring Permissions
              </Typography>
              <Box
                display="flex"
                flexWrap="wrap"
                gap={2}
              >
                <Switch
                  label="Can Access Error Logs"
                  value={entityData.permissions?.canAccessErrorLogs || false}
                  onChange={(newValue) =>
                    updateNestedField(
                      "permissions.canAccessErrorLogs",
                      newValue,
                    )
                  }
                />
                <Switch
                  label="Can Manage Errors"
                  value={entityData.permissions?.canManageErrors || false}
                  onChange={(newValue) =>
                    updateNestedField("permissions.canManageErrors", newValue)
                  }
                />
                <Switch
                  label="Can Assign Errors"
                  value={entityData.permissions?.canAssignErrors || false}
                  onChange={(newValue) =>
                    updateNestedField("permissions.canAssignErrors", newValue)
                  }
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography
                variant="h6"
                sx={{ mb: 1 }}
              >
                Support Ticket Permissions
              </Typography>
              <Box
                display="flex"
                flexWrap="wrap"
                gap={2}
              >
                <Switch
                  label="Can Access Support"
                  value={entityData.permissions?.canAccessSupport || false}
                  onChange={(newValue) =>
                    updateNestedField("permissions.canAccessSupport", newValue)
                  }
                />
                <Switch
                  label="Can Manage Tickets"
                  value={entityData.permissions?.canManageTickets || false}
                  onChange={(newValue) =>
                    updateNestedField("permissions.canManageTickets", newValue)
                  }
                />
                <Switch
                  label="Can Assign Tickets"
                  value={entityData.permissions?.canAssignTickets || false}
                  onChange={(newValue) =>
                    updateNestedField("permissions.canAssignTickets", newValue)
                  }
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        {entityData.role !== "rep" && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle1">Notifications</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography
                variant="h6"
                sx={{ mb: 1 }}
              >
                Notification Preferences
              </Typography>
              <Box
                display="flex"
                flexWrap="wrap"
                gap={2}
              >
                <Switch
                  label="Receive Error Alerts"
                  value={entityData.notifications?.receiveErrorAlerts || false}
                  onChange={(newValue) =>
                    updateNestedField(
                      "notifications.receiveErrorAlerts",
                      newValue,
                    )
                  }
                />
                <Switch
                  label="Receive Support Tickets"
                  value={
                    entityData.notifications?.receiveSupportTickets || false
                  }
                  onChange={(newValue) =>
                    updateNestedField(
                      "notifications.receiveSupportTickets",
                      newValue,
                    )
                  }
                />
                <Switch
                  label="Receive Rep Info Changes"
                  value={entityData.notifications?.receiveUserChanges || false}
                  onChange={(newValue) =>
                    updateNestedField(
                      "notifications.receiveUserChanges",
                      newValue,
                    )
                  }
                />
                <Switch
                  label="Daily Digest"
                  value={entityData.notifications?.dailyDigest || false}
                  onChange={(newValue) =>
                    updateNestedField("notifications.dailyDigest", newValue)
                  }
                />
                <Switch
                  label="Hourly Digest"
                  value={entityData.notifications?.hourlyDigest || false}
                  onChange={(newValue) =>
                    updateNestedField("notifications.hourlyDigest", newValue)
                  }
                />
                <Switch
                  label="In-App Notifications"
                  value={entityData.notifications?.inAppNotifications !== false}
                  onChange={(newValue) =>
                    updateNestedField(
                      "notifications.inAppNotifications",
                      newValue,
                    )
                  }
                />
                <Switch
                  label="Email Enabled"
                  value={entityData.notifications?.emailEnabled !== false}
                  onChange={(newValue) =>
                    updateNestedField("notifications.emailEnabled", newValue)
                  }
                />
              </Box>

              <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Select
                    label="Error Alert Threshold"
                    value={
                      entityData.notifications?.errorAlertThreshold ||
                      "critical"
                    }
                    items={[
                      { id: "critical", name: "Critical" },
                      { id: "high", name: "High" },
                      { id: "medium", name: "Medium" },
                      { id: "low", name: "Low" },
                    ]}
                    onChange={(newValue) =>
                      updateNestedField(
                        "notifications.errorAlertThreshold",
                        newValue,
                      )
                    }
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Select
                    label="Email Format"
                    value={entityData.notifications?.emailFormat || "html"}
                    items={[
                      { id: "html", name: "HTML" },
                      { id: "plain", name: "Plain Text" },
                    ]}
                    onChange={(newValue) =>
                      updateNestedField("notifications.emailFormat", newValue)
                    }
                  />
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        )}
      </Box>
    </Box>
  );
};

export default UserEditor;
